/* eslint-disable no-magic-numbers */
import { MapSize } from '@/redux/map/map.types';
import { AppDispatch } from '@/redux/store';
import { Map, MapBrowserEvent } from 'ol';
import { Comment, ModelElement, NewReaction } from '@/types/models';
import { updateLastClick } from '@/redux/map/map.slice';
import { toLonLat } from 'ol/proj';
import { latLngToPoint } from '@/utils/map/latLngToPoint';
import { closeDrawer } from '@/redux/drawer/drawer.slice';
import { clickHandleAlias } from '@/components/Map/MapViewer/utils/listeners/mouseClick/clickHandleAlias';
import { handleFeaturesClick } from '@/components/Map/MapViewer/utils/listeners/mouseClick/mouseLeftClick/handleFeaturesClick';
import { resetHighlightReactionsData } from '@/redux/reactions/reactions.slice';
import { handleDataReset } from '@/components/Map/MapViewer/utils/listeners/mouseClick/handleDataReset';
import { FEATURE_TYPE } from '@/constants/features';
import { clickHandleReaction } from '@/components/Map/MapViewer/utils/listeners/mouseClick/clickHandleReaction';
import getFeatureAtCoordinate from '@/components/Map/MapViewer/utils/listeners/mouseClick/getFeatureAtCoordinate';
import { clearSearchModelElements } from '@/redux/modelElements/modelElements.slice';
import isEditableElementAtPixel from '@/components/Map/MapViewer/utils/listeners/mouseClick/isEditableElementAtPixel';
import { clearBioEntitySearchData } from '@/redux/bioEntity/bioEntity.slice';
import { dispatchEmptySearchEvents } from '@/services/pluginsManager/pluginsEventBus/dispatchEmptySearchEvents';
import { PluginsEventBus } from '@/services/pluginsManager/pluginsEventBus';
import { getMapClickTarget } from '@/components/Map/MapViewer/utils/listeners/mouseClick/mouseLeftClick/getMapClickTarget';

/* prettier-ignore */
export const onMapLeftClick =
  (
    mapSize: MapSize,
    modelId: number,
    dispatch: AppDispatch,
    isResultDrawerOpen: boolean,
    comments: Comment[],
    modelElements: Array<ModelElement>,
    reactions: Array<NewReaction>,
    activeLayers: number[],
    hasPrivilegeToWriteProject: boolean,
  ) =>
    async (
      { coordinate, pixel }: Pick<MapBrowserEvent, 'coordinate' | 'pixel'>,
      mapInstance: Map,
    ): Promise<void> => {
      const [lng, lat] = toLonLat(coordinate);
      const point = latLngToPoint([lat, lng], mapSize);
      dispatch(updateLastClick({ coordinates: point, modelId }));
      if (hasPrivilegeToWriteProject) {
        const isActiveLayerElementAtPixel = isEditableElementAtPixel({
          mapInstance,
          pixel,
          activeLayers,
        });
        if (isActiveLayerElementAtPixel) {
          // the click is taken by the layer editor; a layer object is not a plugin click target
          PluginsEventBus.dispatchEvent('onMapClick', {
            mapId: modelId,
            x: point.x,
            y: point.y,
            target: null,
          });
          dispatch(handleDataReset);
          dispatch(resetHighlightReactionsData());
          dispatch(clearSearchModelElements());
          return;
        }
      }
      const featureAtCoordinate = getFeatureAtCoordinate({
        mapInstance,
        coordinate,
        point,
        ignoreAllowUiInteraction: hasPrivilegeToWriteProject,
      });
      // sent before the more specific click events for the same click
      PluginsEventBus.dispatchEvent('onMapClick', {
        mapId: modelId,
        x: point.x,
        y: point.y,
        target: getMapClickTarget(featureAtCoordinate),
      });

      if (featureAtCoordinate) {
        const { shouldBlockCoordSearch } = handleFeaturesClick([featureAtCoordinate], dispatch, comments);
        if (shouldBlockCoordSearch) {
          return;
        }
      }

      dispatch(handleDataReset);

      if (!featureAtCoordinate) {
        if (isResultDrawerOpen) {
          dispatch(closeDrawer());
        }

        dispatch(resetHighlightReactionsData());
        dispatch(clearSearchModelElements());
        dispatch(clearBioEntitySearchData());
        dispatchEmptySearchEvents();
        return;
      }

      const type = featureAtCoordinate.get('type');
      const id = featureAtCoordinate.get('id');
      if ([FEATURE_TYPE.ALIAS, FEATURE_TYPE.GLYPH, FEATURE_TYPE.COMPARTMENT].includes(type)) {
        await clickHandleAlias(dispatch)(modelElements, featureAtCoordinate, modelId);
      } else if (type === FEATURE_TYPE.REACTION) {
        clickHandleReaction(dispatch)(modelElements, reactions, id, modelId);
      }
    };
